import { useAuthorizeMonthlyExpenseMutation } from '@/app/services/monthlyExpenseApi';
import { useClosePeriodMutation, usePeriodsByDateQuery } from '@/app/services/periodApi';
import { useStartSharingProcessMutation } from '@/app/services/sharingResultApi';
import PageTitle from '@/components/common/pageTitle';
import GeneralTable from '@/components/General/GeneralTable';
import PeriodDetails from '@/components/periodDetails/PeriodDetails';
import { usePeriod } from '@/context/PeriodContext';
import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

function General() {
  const { selectedPeriod } = usePeriod();
  const { t, i18n } = useTranslation('general');

  const { data: periodsByDate } = usePeriodsByDateQuery(selectedPeriod?.date as string, {
    skip: !selectedPeriod?.date,
  });

  const getPeriodText = () => {
    if (!selectedPeriod || !selectedPeriod.date) return '-- - --';
    const date = DateTime.fromISO(selectedPeriod.date);
    return `${date.setLocale(i18n.language).monthLong} - ${date.year}`;
  };

  const [authorizeSharing] = useStartSharingProcessMutation();
  const [closePeriod] = useClosePeriodMutation();
  const [authorizeMonthlyExpense] = useAuthorizeMonthlyExpenseMutation();

  const authorizeTheSharing = async (period: string) => {
    await toast.promise(
      authorizeSharing(period),
      {
        pending: t('sharing.sent') as string,
        success: t('authorize.sharing.sucess') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const authorizeTheMonthlyExpense = async (period: string) => {
    await toast.promise(
      authorizeMonthlyExpense(period),
      {
        pending: t('monthly.expense.sent') as string,
        success: t('authorize.monthly.expense.sucess') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const closeThePeriod = async (period: string) => {
    toast.info(
      t('close.period.sent', {
        position: toast.POSITION.TOP_RIGHT,
      }),
    );
    toast.dismiss();
    const response = await closePeriod(period);
    if ('data' in response) {
      toast.success(
        t('close.period.sucess', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        mr: 2,
      }}
    >
      <PeriodDetails />
      <PageTitle title={t('general')} titleLabel={` (${t('indirect.sharing')})`} />

      {selectedPeriod && !selectedPeriod.directSharingAuthorized && (
        <Typography variant="body1">{t('not.enough.info', { period: getPeriodText() })}</Typography>
      )}

      {selectedPeriod && selectedPeriod.directSharingAuthorized && !selectedPeriod.expensesAuthorized && (
        <Trans>
          <Typography variant="body1">{t('awaiting.sync', { period: getPeriodText() })}</Typography>
        </Trans>
      )}

      {selectedPeriod &&
        selectedPeriod.directSharingAuthorized &&
        selectedPeriod.expensesAuthorized &&
        periodsByDate && (
          <GeneralTable
            periods={periodsByDate}
            authorizeAction={authorizeTheSharing}
            closePeriodAction={closeThePeriod}
            authorizeMontlyExpenseAction={authorizeTheMonthlyExpense}
          />
        )}
    </Box>
  );
}

export default General;
